// Helpers
import type { AuthStore } from '@/store/auth'
import type { AxiosErrors } from '@/types/Components/Response'
import type { AccountStoreState } from '@/types/Store'
import type { App } from 'vue'
import { notify } from '@kyvg/vue3-notification'
import { CUSTOMER_ROLE_COMPANY_ADMIN, CUSTOMER_ROLE_SHIPMENTS_VIEWER, STAFF_ROLE } from '@/const/AccountRoles'
import { CUSTOMER, STAFF } from '@/const/AccountTypes'
import { CARRIERS_NAMES } from '@/const/Carriers'
import { MAGENTO, ONGOING, SHOPIFY, VISKAN, WOOCOMMERCE } from '@/const/IntegrationTypes'
import { TRACKING_STATUS_NAMES } from '@/const/TrackingEventStatuses'
import { CUSTOMER_HOME_PAGE, LOGIN_PAGE, SCAN_PAGE, STAFF_HOME_PAGE } from '@/types/router'
import moment from 'moment/moment'

let setupAuthStorePromise: Promise<AuthStore> | null = null

const loadSetupAuthStore = async () => {
  if (!setupAuthStorePromise) {
    setupAuthStorePromise = import('@/store/auth').then(({ useAuthStore }) => useAuthStore())
  }

  return setupAuthStorePromise
}

export const ucFirst = (str: string) => (str ? str[0]?.toString().toUpperCase() + str.slice(1) : '')
export const formattedDate = (date: Date | string, format: string) => (date ? moment(date).format(format) : '')
export const readableTime = (date: Date | string | null) => (date ? moment(date).format('HH:mm') : '')
export const toTimeFromNow = (date: Date | string | null) => (date ? moment(date).fromNow() : '')
export const capitalize = (value: string) => (value ? value.toUpperCase() : value)
export const carrierHandler2Name = (carrierHandler: string) => CARRIERS_NAMES[carrierHandler] || carrierHandler
export const trackingStatus2Name = (status: string, fallback: string) => TRACKING_STATUS_NAMES[status] || fallback
export const pluralize = (singular: string, amount: number, plural?: string) =>
  amount > 1 || amount === 0 ? plural || `${singular}s` : singular

export const removeEmpty = (obj: any): object => {
  return Object.fromEntries(
    Object.entries(obj)
      .filter(([, v]) => v)
      .map(([k, v]) => [k, v === Object(v) ? removeEmpty(v) : v]),
  )
}

export const readableDate = (date: Date | string | null) => {
  if (!date) return date

  const parsed = moment(date)
  const format = parsed.year() !== moment().year() ? 'DD MMM YY' : 'DD MMM'

  return parsed.format(format)
}

export const readableDateTime = (date: string | null) => {
  if (!date) return date

  const parsed = moment(date)
  const format = parsed.year() !== moment().year() ? 'DD MMM YY h:mm' : 'DD MMM h:mm'

  return parsed.format(format)
}

export const fileNameTrim = (input: string) => {
  if (!input) {
    return ''
  }

  const parts = input.split('/')

  const last = parts[parts.length - 1]
  if (!last) {
    return ''
  }

  if (last.length <= 25) {
    return last
  }

  return `${last.slice(0, 10)}...${last.slice(-15)}`
}

export const none = () => {}

export const catchErrors = (e: AxiosErrors) => {
  let message = 'An error occurred'
  if (e.message) {
    message = e.message
  } else if (e.errors && Object.keys(e.errors).length) {
    const errors = Object.keys(e.errors)
    errors.forEach((field: string) => {
      e.errors![field].forEach((text: string) => {
        message += `\n${field}: ${text}`
      })
    })
  }

  notify({ group: 'error', text: message, duration: 6000 })
}

export const numbersOnly = (value: string) => value.replace(/[^0-9_@./)(#&+-]+/gm, '')

const currencyFormatters = new Map<string, Intl.NumberFormat>()

const parseLocalizedNumber = (value: string): number => {
  const normalized = value.trim().replace(/\s/g, '')
  const commaIndex = normalized.lastIndexOf(',')
  const dotIndex = normalized.lastIndexOf('.')

  if (commaIndex >= 0 && dotIndex >= 0) {
    return Number(
      commaIndex > dotIndex
        ? normalized.replace(/\./g, '').replace(',', '.')
        : normalized.replace(/,/g, ''),
    )
  }

  return Number(commaIndex >= 0 ? normalized.replace(',', '.') : normalized)
}

export const formattedPrice = (price: any, currency = 'SEK', locale = 'sv-SE') => {
  if (price === null || price === undefined) return '-'
  const stringPrice = String(price)
  if (!stringPrice.trim()) return '-'

  const numericPrice = parseLocalizedNumber(stringPrice)
  if (!Number.isFinite(numericPrice)) return '-'

  const formatterKey = `${locale}:${currency}`
  let formatter = currencyFormatters.get(formatterKey)
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
    })
    currencyFormatters.set(formatterKey, formatter)
  }

  return formatter.format(numericPrice)
}

export const formattedNumber = (value: number | null) => {
  if (value) {
    return parseFloat(String(value)).toLocaleString('sv-SE', {
      maximumFractionDigits: 2,
    })
  }
  return 0
}

export const setupApp = async (app?: App) => {
  const authStore = await loadSetupAuthStore()
  try {
    const baseUrl = import.meta.env.VITE_CDN_URL ? import.meta.env.VITE_CDN_URL : ''

    const response = await fetch(baseUrl + '/version.txt', { cache: 'no-store' })
    const latestVersion = await response.text()
    const currentVersion = localStorage.getItem('app-version')

    if (latestVersion && currentVersion !== latestVersion) {
      localStorage.setItem('app-version', latestVersion)
      window.location.reload() // force reset cache
    } else {
      await authStore.check()
      app?.mount('#app')
    }
  } catch {
    await authStore.check()
    app?.mount('#app')
  }
}

export const getSystemDisplayName = (system: string) => {
  switch (system) {
    case SHOPIFY:
      return 'Shopify'
    case WOOCOMMERCE:
      return 'WooCommerce'
    case MAGENTO:
      return 'Magento'
    case ONGOING:
      return 'OnGoing'
    case VISKAN:
      return 'Viskan'
    default:
      return system
  }
}

export const formatLogMessage = (log: string) => {
  const firstBrace = log.indexOf('{')
  if (firstBrace === -1) {
    return { text: log, json: null, jsonObject: null }
  }

  const textPart = log.slice(0, firstBrace).trim()
  const jsonPart = log.slice(firstBrace)

  let formattedJson
  let parsed
  try {
    parsed = JSON.parse(jsonPart)
    formattedJson = JSON.stringify(parsed, null, 2) // pretty print with 2 spaces
  } catch {
    parsed = null
    formattedJson = jsonPart // fallback raw string
  }

  return {
    text: textPart,
    json: formattedJson,
    jsonObject: parsed,
  }
}

export const resolveHomePage = (account: AccountStoreState['account'] | null) => {
  switch (account?.type) {
    case STAFF:
      return account?.isScannerOnlyEnabled ? SCAN_PAGE : STAFF_HOME_PAGE
    case CUSTOMER:
      return CUSTOMER_HOME_PAGE
    default:
      return LOGIN_PAGE
  }
}

/**
 * Format a shipping error for display.
 * New data is plain text — returned as-is.
 * Legacy data may be JSON — parsed and flattened into a readable string.
 */
export const formatShippingError = (error: string | null | undefined): string => {
  if (!error) return ''
  if (error.charAt(0) !== '{' && error.charAt(0) !== '[') return error

  try {
    const parsed = JSON.parse(error)
    if (!parsed) return error
    const values = Object.values(parsed?.messages || parsed).flat()
    return values.join(', ') || error
  } catch {
    return error
  }
}

const roleOptions = [
  { value: CUSTOMER_ROLE_COMPANY_ADMIN, label: 'Company admin', type: CUSTOMER, badgeVariant: 'success' },
  { value: CUSTOMER_ROLE_SHIPMENTS_VIEWER, label: 'Shipments viewer', type: CUSTOMER, badgeVariant: 'secondary' },
  { value: STAFF_ROLE, label: 'Staff', type: STAFF, badgeVariant: 'success' },
]

export const getRolesFor = (type: typeof CUSTOMER | typeof STAFF) => roleOptions.filter((role) => role.type === type)

export const getRoleLabel = (role: string): string => {
  const option = roleOptions.find((item) => item.value === role)
  return option?.label || role
}

export const getRoleBadgeVariant = (role: string): string => {
  const option = roleOptions.find((item) => item.value === role)
  return option?.badgeVariant || 'secondary'
}
